/**
 * SPLH Dashboard Configuration Loader
 *
 * Provides client-specific configuration by merging default settings
 * with client-specific overrides.
 *
 * @module pages/common/dashboards/SPLHDashboard/config
 */

import dem50Config from './dem50';
import defaultConfig from './default';

/**
 * Client configuration map
 * @type {Object}
 */
const configs = {
  dem50: dem50Config,
  default: defaultConfig,
};

/**
 * Get SPLH Dashboard configuration for a specific client
 *
 * Merges default configuration with client-specific overrides.
 * If client is not found, returns default configuration.
 *
 * @param {string} [client='default'] - Client identifier (e.g., 'dem50')
 * @returns {Object} Merged configuration object with display settings
 *
 * @example
 * import { getSPLHConfig } from './config';
 *
 * const config = getSPLHConfig('dem50');
 * console.log(config.display.chartColors.splh); // '#7C3AED'
 *
 * @example
 * // With custom client config
 * const customConfig = getSPLHConfig('dem50');
 * // Returns: { ...defaultConfig, ...dem50Config }
 */
export const getSPLHConfig = (client = 'default') => {
  const clientConfig = configs[client] || {};
  return {
    ...defaultConfig,
    ...clientConfig,
    // Deep merge display settings
    display: {
      ...defaultConfig.display,
      ...(clientConfig.display || {}),
      chartColors: {
        ...defaultConfig.display.chartColors,
        ...(clientConfig.display?.chartColors || {}),
      },
    },
  };
};

export default getSPLHConfig;
